/**
 * Created by Vinicius
 */

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { fromEvent } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';

import {
  CustomFoodData,
  CustomFoodFilter,
} from 'app/@core/interfaces/common/CalorieFriend/custom-food';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { CustomFoodComponent } from 'app/admin/public-foods/custom-food/custom-food.component';

@Component({
  selector: 'app-food-list',
  templateUrl: './food-list.component.html',
  styleUrls: ['./food-list.component.scss'],
})
export class FoodListComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() readonly: boolean = true;
  @Input() set publicOnly(value: boolean) {
    if (value === undefined) delete this.paging.filterByOnlyPublic;
    else this.paging.filterByOnlyPublic = value;

    // default sort and load data initially
    this.sortor.sort({
      id: this.paging.sortBy,
      start: this.paging.orderBy,
    } as MatSortable);
  }

  @ViewChild(MatPaginator, { static: true }) paginator?: MatPaginator;
  @ViewChild(MatSort, { static: true }) sortor?: MatSort;
  @ViewChild('search', { static: true }) searchInput?: ElementRef;

  readonly displayedColumns = [
    'image',
    'name',
    'description',
    'updatedDate',
    'actions',
  ];
  totalCount: number = 0;
  dataList: any[] = [];
  editUrl: string;
  contentLoaded:boolean = false;

  private unsubscribe$ = new Subject<void>();
  private paging: CustomFoodFilter = { sortBy: 'updatedDate', orderBy: 'desc' };

  constructor(
    private customFoodService: CustomFoodData,
    private toasterService: NbToastrService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((x) => x !== this.paging.filterByNameOrDescription),
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.paging.filterByNameOrDescription =
          this.searchInput.nativeElement.value;
        this.loadDataList();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSortChanged(sort: Sort): void {
    this.paging.sortBy = sort.active;
    this.paging.orderBy = sort.direction;
    this.loadDataList(true);
  }

  loadDataList(pagination: boolean = false): void {
    if (!pagination && this.paginator) this.paginator.pageIndex = 0;
    this.paging.pageNumber = pagination
      ? this.paginator?.pageIndex + 1 ?? 1
      : 1;
    this.paging.pageSize = this.paginator?.pageSize ?? 10;

    this.customFoodService
      .list(this.paging)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.totalCount = res.totalCount;
        this.dataList = res.items;
        this.contentLoaded = true;
      });
  }

  onAddCustomFood(): void {
    if (this.readonly) return;
    this.dialogService
      .open(CustomFoodComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
      })
      .onClose.subscribe(() => this.loadDataList(true));
  }

  onEdit(id: number): void {
    this.dialogService
      .open(CustomFoodComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: { id },
      })
      .onClose.subscribe(() => this.loadDataList(true));
  }

  onDelete(id: number): void {
    if (this.readonly) return;
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this custom food?' },
      })
      .onClose.subscribe((res) => {
        if (!res) return;
        this.customFoodService
          .delete(id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this.toasterService.success(
                '',
                'Successfully deleted Custom Food',
              );
              this.loadDataList(true);
            },
            () =>
              this.toasterService.danger(
                '',
                'Problem occurred delelting Custom Food',
              ),
          );
      });
  }
}
